import type { ComponentPropsWithRef, Ref } from 'react';
import { ProductCard as ProductCardUI } from '@/features/product-card';
import { ProductProvider } from '@/providers/product-context';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import { routeHref, routes } from '@/route-paths';
import { getPrimaryProductImageUrl } from '@/lib/product/product-utils';
import { getProductFromGridItem, type ProductCardItem } from '../../../lib/product-card-adapter';

export type ProductCardProps = {
    product: ProductCardItem;
    priority?: boolean;
    onProductClick?: (product: ProductCardItem) => void;
    getProductUrl?: (product: ProductCardItem) => string;
} & Pick<ComponentPropsWithRef<'article'>, 'className'> & { ref?: Ref<HTMLElement> };

export const ProductCard = ({
    product: item,
    priority = false,
    onProductClick,
    getProductUrl,
    className,
    ref,
}: ProductCardProps) => {
    const product = getProductFromGridItem(item);
    const productUrl =
        getProductUrl?.(item) ??
        routeHref(routes.product, { productId: 'productId' in item ? item.productId : item.id } as {
            productId: string;
        });
    const imageUrl = getPrimaryProductImageUrl(product);

    return (
        <ProductProvider product={product}>
            <ProductCardUI.Root ref={ref} className={cn('border-r border-b border-snk-greige', className)}>
                <Link
                    to={productUrl}
                    className="flex h-full flex-col focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                    onClick={() => onProductClick?.(item)}>
                    <ProductCardUI.Image
                        src={imageUrl}
                        priority={priority}
                        className="size-full"
                        imageClassName="object-contain"
                    />
                    <ProductCardUI.Details>
                        <ProductCardUI.Brand>{product.brand}</ProductCardUI.Brand>
                        <ProductCardUI.Name>{product.name ?? product.id}</ProductCardUI.Name>
                        <ProductCardUI.Price />
                    </ProductCardUI.Details>
                </Link>
            </ProductCardUI.Root>
        </ProductProvider>
    );
};
